import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { groupDrafts, type DraftGroup } from "@/lib/draftBatches";

const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const WRT6042_DEFAULTS = {
  width: '42"',
  height: '67"',
  firebox_opening_height: '30"',
  cabinet_width: '51-1/8"',
  cabinet_height: '49"',
  smoke_dome_top_width: '30-1/2"',
};

interface Draft {
  id: string;
  manufacturer_name: string;
  product_name: string;
  sku: string;
  dimensions: Record<string, string>;
  selected_finishes: string[];
  staging_glb_url: string | null;
  status: "pending_review" | "approved" | "rejected";
  flags: string[] | null;
  approved_product_id: string | null;
  created_at: string;
  // M2 additions. A generated matrix arrives as N draft rows sharing a
  // batch_id (Q-A: a variant IS a draft); `parameters` carries pergola
  // parameters where `dimensions` carries the fireplace ones.
  batch_id: string | null;
  parameters: Record<string, unknown> | null;
  template_id: string | null;
  finish_id: string | null;
}

const DIMENSION_FIELDS: { key: keyof typeof WRT6042_DEFAULTS; label: string }[] = [
  { key: "width", label: "Firebox opening width (G)" },
  { key: "cabinet_width", label: "Cabinet width (H)" },
  { key: "smoke_dome_top_width", label: "Smoke dome top width (I)" },
  { key: "firebox_opening_height", label: "Firebox opening height (K)" },
  { key: "cabinet_height", label: "Cabinet height (L)" },
  { key: "height", label: "Overall height (M)" },
];

/**
 * Wraps a group of drafts. A standalone draft (no batch_id) renders with no
 * chrome at all, so the shipped one-at-a-time fireplace flow looks exactly as
 * it did before batching existed. Only a real multi-draft batch gets a header
 * and a bulk action.
 */
function BatchShell({
  group, busy, progress, onApproveAll, children,
}: {
  group: DraftGroup<Draft>;
  busy: boolean;
  progress: { done: number; total: number } | null;
  onApproveAll: () => void;
  children: React.ReactNode;
}) {
  if (!group.isBatch) return <>{children}</>;

  const { counts, approvable, drafts } = group;
  const blocked = counts.pending - approvable.length;

  return (
    <div className="border-2 border-blue-200 rounded-xl p-4 space-y-3 bg-blue-50/30">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium">
            {group.label}{" "}
            <span className="text-sm font-normal text-gray-500">
              — batch of {drafts.length}
            </span>
          </p>
          <p className="text-xs text-gray-500">
            {counts.pending} pending · {counts.approved} approved · {counts.rejected} rejected
          </p>
        </div>
        {approvable.length > 0 && (
          <div className="flex flex-col items-end gap-1">
            <button
              className="bg-green-600 text-white rounded px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={busy}
              onClick={onApproveAll}
            >
              {busy && progress
                ? `Approving ${progress.done + 1} of ${progress.total}...`
                : `Approve all ${approvable.length} pending`}
            </button>
            {blocked > 0 && (
              <p className="text-xs text-amber-600">
                {blocked} skipped — no staged GLB yet
              </p>
            )}
          </div>
        )}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export default function AdminParametricDrafts() {
  const { session } = useAuth();
  const jwt = session?.access_token ?? "";

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [manufacturerName, setManufacturerName] = useState("Superior Fireplaces");
  const [productName, setProductName] = useState("WRT6042");
  const [sku, setSku] = useState("WRT6042");
  const [dimensions, setDimensions] = useState<Record<string, string>>(WRT6042_DEFAULTS);
  const [finishesText, setFinishesText] = useState(
    "Ivory Split Stack, Midnight Full Herringbone, Warm Red Full Stack, Slate Grey Split Herringbone",
  );
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyDraftId, setBusyDraftId] = useState<string | null>(null);
  const [busyBatchId, setBusyBatchId] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  const [approveMessage, setApproveMessage] = useState<string | null>(null);

  const loadDrafts = useCallback(async () => {
    const res = await fetch(
      `${SB_URL}/rest/v1/parametric_asset_drafts?select=*&order=created_at.desc`,
      { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
    );
    const data = await res.json();
    setDrafts(Array.isArray(data) ? (data as Draft[]) : []);
  }, [jwt]);

  useEffect(() => { if (jwt) loadDrafts(); }, [jwt, loadDrafts]);

  const groups = useMemo(() => groupDrafts(drafts), [drafts]);

  async function callEdgeFunction(name: string, body: unknown) {
    const res = await fetch(`${SB_URL}/functions/v1/${name}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
        apikey: ANON_KEY,
      },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error ?? `Request failed (${res.status})`);
    return json;
  }

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const selected_finishes = finishesText.split(",").map((s) => s.trim()).filter(Boolean);
      await callEdgeFunction("generate-parametric-preview", {
        manufacturer_name: manufacturerName,
        product_name: productName,
        sku,
        dimensions,
        selected_finishes,
      });
      await loadDrafts();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setGenerating(false);
    }
  }

  async function handleApprove(draftId: string) {
    setBusyDraftId(draftId);
    setError(null);
    setApproveMessage(null);
    try {
      // approve-parametric-draft now invokes process-parametric-approval
      // asynchronously (InvocationType: Event) rather than waiting for the
      // full PBR + glb-assembly run to finish, so this response only
      // confirms approval was accepted and processing started — not that
      // the product is fully textured/assembled yet. Progress is checked
      // directly against the product row, not polled here.
      await callEdgeFunction("approve-parametric-draft", { draft_id: draftId });
      setApproveMessage("Approval started — check back shortly. PBR and glb-assembly run in the background; check the linked product's status directly for completion.");
      await loadDrafts();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyDraftId(null);
    }
  }

  /**
   * Approves every approvable draft in a batch, one at a time.
   *
   * Sequential on purpose: each approval fans out to process-parametric-approval,
   * which regenerates geometry, bakes a 45-tile shadow atlas and triggers
   * glb-assembly. Firing 21 of those at once would stampede the Lambda and the
   * R2 write path for no operator benefit. Failures are collected rather than
   * aborting, so one bad draft does not strand the other twenty in limbo with
   * no record of which went through.
   */
  async function handleApproveBatch(batchId: string, drafts: Draft[]) {
    setBusyBatchId(batchId);
    setError(null);
    setApproveMessage(null);

    const failures: string[] = [];
    let approved = 0;
    for (const d of drafts) {
      setBatchProgress({ done: approved + failures.length, total: drafts.length });
      try {
        await callEdgeFunction("approve-parametric-draft", { draft_id: d.id });
        approved++;
      } catch (err) {
        failures.push(`${d.sku}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }

    setBatchProgress(null);
    setBusyBatchId(null);
    setApproveMessage(
      `Approved ${approved} of ${drafts.length}. Processing runs in the background — ` +
      `check each product's pipeline status for completion.`,
    );
    if (failures.length) setError(`${failures.length} failed — ${failures.join("; ")}`);
    await loadDrafts();
  }

  async function handleReject(draftId: string) {
    setBusyDraftId(draftId);
    setError(null);
    try {
      // Direct RLS-protected PATCH — no edge function needed for reject.
      const res = await fetch(`${SB_URL}/rest/v1/parametric_asset_drafts?id=eq.${draftId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${jwt}`,
          Prefer: "return=minimal",
        },
        body: JSON.stringify({ status: "rejected" }),
      });
      if (!res.ok) throw new Error(`Reject failed (${res.status})`);
      await loadDrafts();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyDraftId(null);
    }
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <h1 className="text-2xl font-semibold">Generate from Spec Dimensions</h1>

      <section className="space-y-4 border rounded-lg p-6">
        <h2 className="text-lg font-medium">New draft</h2>
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">Manufacturer</span>
            <input className="border rounded px-3 py-2" value={manufacturerName} onChange={(e) => setManufacturerName(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">Product name</span>
            <input className="border rounded px-3 py-2" value={productName} onChange={(e) => setProductName(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-500">SKU</span>
            <input className="border rounded px-3 py-2" value={sku} onChange={(e) => setSku(e.target.value)} />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {DIMENSION_FIELDS.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-sm text-gray-500">{label}</span>
              <input
                className="border rounded px-3 py-2"
                value={dimensions[key] ?? ""}
                onChange={(e) => setDimensions((d) => ({ ...d, [key]: e.target.value }))}
              />
            </label>
          ))}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-500">Available finishes (comma-separated)</span>
          <input className="border rounded px-3 py-2" value={finishesText} onChange={(e) => setFinishesText(e.target.value)} />
        </label>

        <button
          className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50"
          disabled={generating}
          onClick={handleGenerate}
        >
          {generating ? "Generating..." : "Generate Preview"}
        </button>
        {error && <p className="text-red-600 text-sm">{error}</p>}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-medium">Drafts</h2>
        {approveMessage && <p className="text-green-700 text-sm">{approveMessage}</p>}
        {groups.map((g) => (
          <BatchShell
            key={g.batchId ?? g.drafts[0].id}
            group={g}
            busy={busyBatchId === g.batchId}
            progress={busyBatchId === g.batchId ? batchProgress : null}
            onApproveAll={() => handleApproveBatch(g.batchId!, g.approvable)}
          >
        {g.drafts.map((d) => (
          <div key={d.id} className="border rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium">{d.product_name} ({d.sku}) — {d.manufacturer_name}</p>
                <p className="text-sm text-gray-500">{d.selected_finishes.join(", ")}</p>
                <p className="text-xs text-gray-400 uppercase">{d.status}</p>
              </div>
              {d.staging_glb_url && (
                <a href={d.staging_glb_url} download className="text-blue-600 text-sm underline">
                  Download GLB
                </a>
              )}
            </div>
            {d.flags && d.flags.length > 0 && (
              <ul className="text-xs text-amber-600 list-disc pl-4">
                {d.flags.map((f) => <li key={f}>{f}</li>)}
              </ul>
            )}
            {d.status === "pending_review" && (
              <div className="flex flex-col gap-1">
                <div className="flex gap-2">
                  <button
                    className="bg-green-600 text-white rounded px-3 py-1 text-sm disabled:opacity-50"
                    disabled={busyDraftId === d.id || !d.staging_glb_url}
                    title={!d.staging_glb_url ? "No preview GLB available yet — cannot approve" : undefined}
                    onClick={() => handleApprove(d.id)}
                  >
                    {busyDraftId === d.id ? "Approving..." : "Approve"}
                  </button>
                  <button
                    className="bg-gray-200 rounded px-3 py-1 text-sm disabled:opacity-50"
                    disabled={busyDraftId === d.id}
                    onClick={() => handleReject(d.id)}
                  >
                    Reject
                  </button>
                </div>
                {!d.staging_glb_url && (
                  <p className="text-xs text-amber-600">
                    No preview GLB yet — preview generation may have failed or is incomplete. Approve is disabled until a staged GLB exists.
                  </p>
                )}
              </div>
            )}
            {d.approved_product_id && (
              <p className="text-xs text-gray-500">Product: {d.approved_product_id}</p>
            )}
          </div>
        ))}
          </BatchShell>
        ))}
      </section>
    </div>
  );
}
